import { useEffect } from 'react';

export interface UseUndoShortcutsOptions {
  undo(): void;
  redo(): void;
  /** True while undo/redo would act on something not on screen, such as a version preview. */
  disabled: boolean;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable === true
  );
}

/** Global Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z and Ctrl/Cmd+Y redo. A field
 *  being typed in keeps its own native undo. */
export function useUndoShortcuts(options: UseUndoShortcutsOptions): void {
  const { undo, redo, disabled } = options;
  useEffect(() => {
    if (disabled) return;
    function onKeyDown(event: KeyboardEvent): void {
      // Mid-composition the keystroke belongs to the input method, not to undo.
      if (event.isComposing) return;
      if (!(event.ctrlKey || event.metaKey)) return;
      const key = event.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      if (isTypingTarget(event.target)) return;
      event.preventDefault();
      if (key === 'y' || event.shiftKey) redo();
      else undo();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo, disabled]);
}
